import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getFileUrl } from '../utils/fileUtils';
import API from '../utils/api';
import { 
  FaUser, 
  FaEnvelope, 
  FaPhone, 
  FaMapMarkerAlt, 
  FaCheckCircle,
  FaExclamationCircle,
  FaEdit
} from 'react-icons/fa';
import { toast } from 'react-toastify';

const Profile = () => {
  const { user: authUser, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [resumeFile, setResumeFile] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    bio: '',
    skills: [],
    location: {
      city: '',
      state: '',
      country: ''
    }
  });
  const [completionData, setCompletionData] = useState({
    percentage: 0,
    completed: [],
    pending: []
  });

  useEffect(() => {
    if (!isAuthenticated) {
      toast.error('Please login to view profile');
      navigate('/login');
      return;
    }
    fetchProfile();
  }, [isAuthenticated, navigate]);

  const fetchProfile = async () => {
    try {
      const { data } = await API.get('/users/profile');
      setUser(data);
      calculateCompletion(data);
    } catch (error) {
      console.error('Error fetching profile:', error);
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const calculateCompletion = (userData) => {
    const checks = [
      { name: 'Basic Info', field: userData.name && userData.email, weight: 10 },
      { name: 'Phone Number', field: userData.phone, weight: 10 },
      { name: 'Profile Photo', field: userData.avatar && userData.avatar !== 'default-avatar.png', weight: 10 },
      { name: 'Location', field: userData.location?.city, weight: 10 },
      { name: 'Bio', field: userData.bio, weight: 15 },
      { name: 'Skills', field: userData.skills?.length > 0, weight: 15 },
      { name: 'Resume', field: userData.resume, weight: 15 },
      { name: 'Experience', field: userData.experience?.length > 0, weight: 10 },
      { name: 'Education', field: userData.education?.length > 0, weight: 15 },
    ];

    const completed = checks.filter(check => check.field);
    const pending = checks.filter(check => !check.field);
    const calculatedPercentage = completed.reduce((sum, item) => sum + item.weight, 0);
    // Cap percentage at 100
    const percentage = Math.min(calculatedPercentage, 100);

    setCompletionData({
      percentage,
      completed: completed.map(c => c.name),
      pending: pending.map(p => ({ name: p.name, weight: p.weight }))
    });
  };

  const handleEditProfile = () => {
    setFormData({
      name: user.name || '',
      phone: user.phone || '',
      bio: user.bio || '',
      skills: user.skills || [],
      location: {
        city: user.location?.city || '',
        state: user.location?.state || '',
        country: user.location?.country || ''
      }
    });
    setIsEditModalOpen(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSkillsChange = (e) => {
    const skillsArray = e.target.value.split(',').map(skill => skill.trim()).filter(skill => skill);
    setFormData(prev => ({
      ...prev,
      skills: skillsArray
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size should be less than 5MB');
        return;
      }
      // Check file type
      const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      if (!allowedTypes.includes(file.type)) {
        toast.error('Only PDF and DOC/DOCX files are allowed');
        return;
      }
      setResumeFile(file);
    }
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    try {
      // First update profile data
      const { data } = await API.put('/users/profile', formData);
      
      // Then upload resume if a new file was selected
      if (resumeFile) {
        const formDataFile = new FormData();
        formDataFile.append('resume', resumeFile);
        await API.post('/users/resume', formDataFile, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
      }
      
      // Fetch updated profile
      const { data: updatedUser } = await API.get('/users/profile');
      setUser(updatedUser);
      calculateCompletion(updatedUser);
      setIsEditModalOpen(false);
      setResumeFile(null);
      toast.success('Profile updated successfully!');
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error(error.response?.data?.message || 'Failed to update profile');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) return null;

  const getCompletionColor = () => {
    if (completionData.percentage >= 80) return 'bg-green-500';
    if (completionData.percentage >= 50) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  const getCompletionTextColor = () => {
    if (completionData.percentage >= 80) return 'text-green-600';
    if (completionData.percentage >= 50) return 'text-yellow-600';
    return 'text-red-600';
  };

  return (
    <div className="min-h-screen bg-gray-50/50 pb-16">
      {/* ── TOP GRADIENT BANNER ── */}
      <div className="bg-gradient-to-r from-[#00c2ab] to-[#009688] pt-12 pb-24 px-4 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-full opacity-10">
          <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="w-full h-full">
            <polygon fill="white" points="0,100 100,0 100,100" />
          </svg>
        </div>
        <div className="container mx-auto max-w-5xl relative z-10">
          <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-2 drop-shadow-md">
            Welcome back, {user?.name || 'User'}!
          </h1>
          <p className="text-teal-50 text-lg">
            {user?.role === 'employer' ? 'Manage your company profile and job postings.' : 'Manage your professional profile and track your progress.'}
          </p>
        </div>
      </div>

      <div className="container mx-auto px-4 -mt-16 relative z-20">
        <div className="max-w-5xl mx-auto">
          {/* For Employers - Dashboard Overview */}
          {user?.role === 'employer' ? (
            <div className="grid md:grid-cols-2 gap-8">
              {/* Card 1: Company Profile */}
              <div 
                onClick={() => navigate(user?.company ? '/company-profile' : '/create-company')}
                className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 p-8 cursor-pointer border border-gray-100 group flex flex-col justify-between"
              >
                <div>
                  <div className="w-16 h-16 bg-teal-50 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-teal-100 transition-colors">
                    <FaUser className="text-[#00c2ab] text-2xl" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-[#00c2ab] transition-colors">
                    Company Profile
                  </h2>
                  <p className="text-gray-600 mb-8 leading-relaxed">
                    As an employer, your company profile acts as your main identity. Manage details, branding, and culture to attract top talent.
                  </p>
                </div>
                <button className="w-full bg-[#00c2ab] text-white py-3.5 rounded-xl font-semibold shadow-md hover:bg-[#00a894] transition-all flex items-center justify-center gap-2">
                  {user?.company ? 'View Company Profile' : 'Create Company Profile'}
                </button>
              </div>
              
              {/* Card 2: Manage Jobs */}
              <div 
                onClick={() => navigate('/my-jobs')}
                className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 p-8 cursor-pointer border border-gray-100 group flex flex-col justify-between"
              >
                <div>
                  <div className="w-16 h-16 bg-teal-50 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-teal-100 transition-colors">
                    <FaEnvelope className="text-[#00c2ab] text-2xl" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-[#00c2ab] transition-colors">
                    Manage Posted Jobs
                  </h2>
                  <p className="text-gray-600 mb-8 leading-relaxed">
                    Review incoming applications, update active job listings, and easily post new opportunities for your team.
                  </p>
                </div>
                <button className="w-full border-2 border-[#00c2ab] text-[#00c2ab] py-3.5 rounded-xl font-semibold hover:bg-teal-50 transition-all flex items-center justify-center gap-2">
                  Manage Posted Jobs
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-8">
              {/* Profile Completion Card */}
              <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Profile Completion</h2>
                <p className="text-gray-600">
                  {completionData.percentage >= 80 
                    ? 'Excellent! Your profile is looking great 🎉' 
                    : completionData.percentage >= 50 
                    ? 'Good progress! Keep going 💪'
                    : 'Let\'s complete your profile to get better matches 🚀'
                  }
                </p>
              </div>
              <div className="text-right">
                <div className="text-5xl font-extrabold text-[#00c2ab]">{completionData.percentage}%</div>
                <div className="text-sm font-medium text-gray-500 uppercase tracking-wider mt-1">Complete</div>
              </div>
            </div>

            {/* Progress Bar */}
            <div className="w-full bg-gray-100 rounded-full h-4 mb-6 shadow-inner">
              <div 
                className="bg-gradient-to-r from-[#00c2ab] to-[#009688] h-4 rounded-full transition-all duration-700 ease-out shadow-[0_0_10px_rgba(0,194,171,0.5)]"
                style={{ width: `${completionData.percentage}%` }}
              ></div>
            </div>

            {/* Pending Items */}
            {completionData.pending.length > 0 && (
              <div className="bg-teal-50/50 rounded-xl p-5 border border-teal-100">
                <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <FaExclamationCircle className="text-[#00c2ab]" />
                  Complete these to boost your profile:
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {completionData.pending.map((item, index) => (
                    <div key={index} className="flex items-center gap-3 text-sm text-gray-700 bg-white p-3 rounded-lg border border-teal-50 shadow-sm">
                      <span className="w-2.5 h-2.5 bg-[#00c2ab] rounded-full shadow-sm"></span>
                      <span className="flex-1 font-medium">{item.name}</span>
                      <span className="text-[#00c2ab] font-bold bg-teal-50 px-2 py-0.5 rounded-md">+{item.weight}%</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Profile Information */}
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
            <div className="flex items-center justify-between mb-8 pb-4 border-b border-gray-100">
              <h2 className="text-2xl font-bold text-gray-900">Profile Information</h2>
              <button 
                onClick={handleEditProfile}
                className="bg-white border-2 border-[#00c2ab] text-[#00c2ab] hover:bg-teal-50 px-5 py-2.5 rounded-xl font-semibold flex items-center gap-2 transition-colors shadow-sm"
              >
                <FaEdit /> Edit Profile
              </button>
            </div>

            <div className="space-y-6">
              {/* Basic Info */}
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="text-sm text-gray-600 mb-1 flex items-center gap-2">
                    <FaUser className="text-gray-400" /> Full Name
                  </label>
                  <p className="text-lg font-medium text-gray-900">{user.name || 'Not provided'}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-600 mb-1 flex items-center gap-2">
                    <FaEnvelope className="text-gray-400" /> Email
                  </label>
                  <p className="text-lg font-medium text-gray-900">{user.email}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-600 mb-1 flex items-center gap-2">
                    <FaPhone className="text-gray-400" /> Phone
                  </label>
                  <p className="text-lg font-medium text-gray-900">{user.phone || 'Not provided'}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-600 mb-1 flex items-center gap-2">
                    <FaMapMarkerAlt className="text-gray-400" /> Location
                  </label>
                  <p className="text-lg font-medium text-gray-900">
                    {user.location?.city ? `${user.location.city}, ${user.location.state || user.location.country || ''}` : 'Not provided'}
                  </p>
                </div>
              </div>

              {/* Bio */}
              {user.bio && (
                <div>
                  <label className="text-sm text-gray-600 mb-2 block">About Me</label>
                  <p className="text-gray-700">{user.bio}</p>
                </div>
              )}

              {/* Skills */}
              {user.skills && user.skills.length > 0 && (
                <div>
                  <label className="text-sm text-gray-600 mb-2 block">Skills</label>
                  <div className="flex flex-wrap gap-2">
                    {user.skills.map((skill, index) => (
                      <span key={index} className="px-3 py-1 bg-teal-50 text-[#00c2ab] border border-teal-100 rounded-full text-sm font-medium shadow-sm">
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Resume */}
              {user.resume && (
                <div className="pt-4">
                  <label className="text-sm text-gray-600 mb-3 block font-medium">Resume / CV</label>
                  <div className="flex items-center gap-4 bg-gray-50 p-5 rounded-xl border border-gray-200 shadow-sm transition-all hover:shadow-md">
                    <div className="w-14 h-14 bg-gradient-to-br from-[#00c2ab] to-[#009688] rounded-xl flex items-center justify-center shadow-inner">
                      <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-gray-900">{user.resume.split('/').pop()}</p>
                      <p className="text-sm text-gray-500 mt-0.5">Ready for applications</p>
                    </div>
                    <div className="flex gap-3">
                      <a 
                        href={`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/${user.resume}`} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="bg-white border-2 border-gray-200 text-gray-700 px-5 py-2 rounded-lg hover:border-[#00c2ab] hover:text-[#00c2ab] font-medium text-sm transition-all"
                      >
                        View
                      </a>
                      <a 
                        href={`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/${user.resume}`} 
                        download
                        className="bg-[#00c2ab] text-white px-5 py-2 rounded-lg hover:bg-[#00a894] font-medium text-sm transition-all shadow-md hover:shadow-lg"
                      >
                        Download
                      </a>
                    </div>
                  </div>
                </div>
              )}
            </div>
                     </div>
          </div>
          )}

          {/* Edit Profile Modal */}
          {isEditModalOpen && (
        <div className="fixed inset-0 bg-gray-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4 transition-opacity">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-gray-100">
            <div className="sticky top-0 bg-white/95 backdrop-blur px-8 py-5 flex items-center justify-between border-b border-gray-100 z-10">
              <h2 className="text-2xl font-bold text-gray-900">Edit Profile</h2>
              <button 
                onClick={() => setIsEditModalOpen(false)}
                className="text-gray-400 hover:text-gray-700 text-3xl font-light hover:rotate-90 transition-transform"
              >
                ×
              </button>
            </div>

            <form onSubmit={handleUpdateProfile} className="p-8">
              <div className="space-y-6">
                {/* Full Name */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Full Name <span className="text-[#00c2ab]">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#00c2ab] focus:border-transparent transition-all shadow-sm"
                    required
                  />
                </div>

                {/* Phone */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#00c2ab] focus:border-transparent transition-all shadow-sm"
                    placeholder="+91XXXXXXXXXX"
                  />
                </div>

                {/* Location */}
                <div className="grid md:grid-cols-3 gap-5">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">City</label>
                    <input
                      type="text"
                      name="location.city"
                      value={formData.location.city}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#00c2ab] focus:border-transparent transition-all shadow-sm"
                      placeholder="e.g. Mumbai"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">State</label>
                    <input
                      type="text"
                      name="location.state"
                      value={formData.location.state}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#00c2ab] focus:border-transparent transition-all shadow-sm"
                      placeholder="e.g. Maharashtra"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Country</label>
                    <input
                      type="text"
                      name="location.country"
                      value={formData.location.country}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#00c2ab] focus:border-transparent transition-all shadow-sm"
                      placeholder="e.g. India"
                    />
                  </div>
                </div>

                {/* Bio */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    About Me (Bio)
                  </label>
                  <textarea
                    name="bio"
                    value={formData.bio}
                    onChange={handleInputChange}
                    rows="4"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#00c2ab] focus:border-transparent transition-all shadow-sm resize-none"
                    placeholder="Tell us about yourself..."
                  ></textarea>
                </div>

                {/* Skills */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Skills <span className="text-gray-400 font-normal">(comma separated)</span>
                  </label>
                  <input
                    type="text"
                    value={formData.skills.join(', ')}
                    onChange={handleSkillsChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#00c2ab] focus:border-transparent transition-all shadow-sm"
                    placeholder="e.g. React, MongoDB, JavaScript, Node.js"
                  />
                  <p className="text-sm text-teal-600 mt-2 font-medium">
                    Current: {formData.skills.length} skill{formData.skills.length !== 1 ? 's' : ''} added
                  </p>
                </div>

                {/* Resume Upload */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Resume / CV
                  </label>
                  <div className="space-y-4">
                    {user.resume && (
                      <div className="flex items-center gap-3 text-sm text-gray-700 bg-teal-50 p-4 rounded-xl border border-teal-100 shadow-sm">
                        <FaCheckCircle className="text-[#00c2ab] text-lg" />
                        <span className="flex-1 font-medium truncate">Current: {user.resume.split('/').pop()}</span>
                        <a 
                          href={`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/${user.resume}`} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="bg-white border-2 border-teal-200 text-[#00c2ab] hover:bg-teal-100 px-4 py-1.5 rounded-lg font-medium text-xs transition-colors"
                        >
                          View
                        </a>
                      </div>
                    )}
                    <div className="relative">
                      <input
                        type="file"
                        accept=".pdf,.doc,.docx"
                        onChange={handleFileChange}
                        className="block w-full text-sm text-gray-500
                          file:mr-4 file:py-2.5 file:px-6
                          file:rounded-xl file:border-0
                          file:text-sm file:font-semibold
                          file:bg-[#00c2ab] file:text-white
                          hover:file:bg-[#00a894]
                          file:cursor-pointer file:transition-colors
                          border border-gray-300 rounded-xl shadow-sm
                          focus:outline-none"
                      />
                    </div>
                    {resumeFile && (
                      <p className="text-sm text-[#00c2ab] flex items-center gap-2 font-medium bg-teal-50 p-3 rounded-xl border border-teal-100">
                        <FaCheckCircle />
                        New file selected: {resumeFile.name}
                      </p>
                    )}
                    <p className="text-xs text-gray-400">
                      Allowed formats: PDF, DOC, DOCX (Max size: 5MB)
                    </p>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-4 mt-8 pt-8 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setIsEditModalOpen(false)}
                  className="flex-1 px-6 py-3.5 border-2 border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 hover:border-gray-300 font-semibold transition-all"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-6 py-3.5 bg-gradient-to-r from-[#00c2ab] to-[#009688] text-white rounded-xl hover:shadow-lg hover:shadow-teal-500/30 font-semibold transition-all transform hover:-translate-y-0.5"
                >
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
        </div>
      </div>
    </div>
  );
};

export default Profile;
